import { useEffect, useState } from 'react';
import { Loader2, FileText, Camera, Check, Printer, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Wasika {
  id: string; code: string;
  status: string;
  bayan_photo_url: string | null;
  bayan_permit_number: string | null;
  bayan_issue_date: string | null;
  bayan_expiry_date: string | null;
  bayan_ocr_status: string | null;
  consignment_number: string | null;
  consignment_pdf_url: string | null;
  consignment_printed_count: number;
  delivery_receipt_photo_url: string | null;
  delivery_receipt_signer_name: string | null;
  delivery_receipt_signer_company: string | null;
  wasika_cost_sar: string | null;
  wasika_cost_paid_by: string | null;
}

interface WasikaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flatbedTripId: string;
  onUpdated?: () => void;
}

export function WasikaModal({ open, onOpenChange, flatbedTripId, onUpdated }: WasikaModalProps) {
  const [wasika, setWasika] = useState<Wasika | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'bayan' | 'consignment' | 'delivery'>('bayan');
  const [permitNumber, setPermitNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiClient.get<{ items: Wasika[] }>(`/api/v1/wasika?flatbedTripId=${flatbedTripId}`);
      const w = r.data.items[0] ?? null;
      setWasika(w);
      if (w) {
        setPermitNumber(w.bayan_permit_number ?? '');
        setIssueDate(w.bayan_issue_date ? w.bayan_issue_date.slice(0, 10) : '');
        setExpiryDate(w.bayan_expiry_date ? w.bayan_expiry_date.slice(0, 10) : '');
        setSignerName(w.delivery_receipt_signer_name ?? '');
        setSignerCompany(w.delivery_receipt_signer_company ?? '');
      }
      setError(null);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatbedTripId]);

  async function createWasika() {
    setBusy(true);
    try {
      await apiClient.post('/api/v1/wasika', { flatbedTripId });
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function uploadBayan(file: File) {
    if (!wasika) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const jwt = getStoredToken();
      const res = await fetch(`${API_BASE}/api/v1/wasika/${wasika.id}/upload-bayan`, {
        method: 'POST',
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      await refresh();
      onUpdated?.();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function saveBayanData() {
    if (!wasika || !permitNumber) return;
    setBusy(true);
    try {
      await apiClient.patch(`/api/v1/wasika/${wasika.id}/bayan-data`, {
        bayanPermitNumber: permitNumber,
        bayanIssueDate:    issueDate || undefined,
        bayanExpiryDate:   expiryDate || undefined,
      });
      await refresh();
      onUpdated?.();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function generateConsignment() {
    if (!wasika) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/wasika/${wasika.id}/generate-consignment`);
      await refresh();
      onUpdated?.();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function printConsignment() {
    if (!wasika) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/wasika/${wasika.id}/print`);
      window.open(`${API_BASE}/api/v1/wasika/${wasika.id}/consignment.pdf`, '_blank');
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function uploadDelivery(file: File) {
    if (!wasika) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (signerName) fd.append('signerName', signerName);
      if (signerCompany) fd.append('signerCompany', signerCompany);
      const jwt = getStoredToken();
      const res = await fetch(`${API_BASE}/api/v1/wasika/${wasika.id}/upload-delivery-receipt`, {
        method: 'POST',
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      await refresh();
      onUpdated?.();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Wasika / Consignment Note</SheetTitle>
          <SheetDescription>{wasika?.code ?? 'Not started'}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
          ) : !wasika ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center space-y-3">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto" />
              <div className="text-sm text-muted-foreground">No wasika yet for this trip.</div>
              <Button onClick={createWasika} disabled={busy}>Start wasika</Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                Status: <strong>{wasika.status.replace(/_/g, ' ')}</strong>
                {wasika.consignment_number && <> · Consignment <strong>{wasika.consignment_number}</strong> generated</>}
              </div>

              <div className="flex gap-2 border-b border-border">
                {([
                  { v: 'bayan',       l: '1. Bayan receipt' },
                  { v: 'consignment', l: '2. Consignment' },
                  { v: 'delivery',    l: '3. Delivery' },
                ] as const).map((x) => (
                  <button key={x.v} onClick={() => setTab(x.v)}
                    className={'px-3 py-2 text-sm border-b-2 -mb-px ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                    {x.l}
                  </button>
                ))}
              </div>

              {tab === 'bayan' && (
                <div className="space-y-3 text-sm">
                  <div>
                    <Label>Upload Bayan photo (from logisti.sa)</Label>
                    <Input type="file" accept="image/*,application/pdf"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBayan(f); }}
                      disabled={busy} />
                    {wasika.bayan_photo_url && <p className="text-[11px] text-muted-foreground mt-1">✓ Uploaded</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Bayan permit number</Label>
                    <Input value={permitNumber} onChange={(e) => setPermitNumber(e.target.value)} placeholder="e.g. BN-2026-12345" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label>Issue date</Label><Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></div>
                    <div className="space-y-1"><Label>Expiry date</Label><Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> OCR auto-extraction is deferred — enter the permit number manually for now.
                  </p>
                  <Button onClick={saveBayanData} disabled={busy || !permitNumber} className="gap-2">
                    <Check className="h-4 w-4" /> Save Bayan data
                  </Button>
                </div>
              )}

              {tab === 'consignment' && (
                <div className="space-y-3 text-sm">
                  {wasika.consignment_number ? (
                    <>
                      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Check className="h-4 w-4" /> Consignment <strong>{wasika.consignment_number}</strong> ready · printed {wasika.consignment_printed_count}×
                      </div>
                      <Button onClick={printConsignment} disabled={busy} className="gap-2"><Printer className="h-4 w-4" /> Print</Button>
                    </>
                  ) : (
                    <Button onClick={generateConsignment} disabled={busy || !wasika.bayan_permit_number} className="gap-2">
                      <FileText className="h-4 w-4" /> Generate consignment note
                    </Button>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    A consignment note must be generated before the trip can move from loaded → in_transit.
                  </p>
                </div>
              )}

              {tab === 'delivery' && (
                <div className="space-y-3 text-sm">
                  <div>
                    <Label>Upload delivery receipt photo (post-delivery)</Label>
                    <Input type="file" accept="image/*,application/pdf"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDelivery(f); }}
                      disabled={busy} />
                    {wasika.delivery_receipt_photo_url && <p className="text-[11px] text-muted-foreground mt-1">✓ Uploaded</p>}
                  </div>
                  <div className="space-y-1"><Label>Signer name</Label><Input value={signerName} onChange={(e) => setSignerName(e.target.value)} /></div>
                  <div className="space-y-1"><Label>Signer company</Label><Input value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} /></div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function WasikaBanner({ flatbedTripId, onOpen, status }: { flatbedTripId: string; onOpen: () => void; status: string | null }) {
  const tone =
    status === 'consignment_generated' || status === 'printed' ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-800 dark:text-emerald-300'
    : status === 'bayan_uploaded' || status === 'bayan_extracted' ? 'border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-300'
    : 'border-rose-500/30 bg-rose-500/5 text-rose-800 dark:text-rose-300';
  const label =
    status === 'consignment_generated' || status === 'printed' ? '✓ Consignment note ready'
    : status === 'bayan_extracted' ? 'Bayan data saved — generate consignment note'
    : status === 'bayan_uploaded' ? 'Bayan uploaded — enter permit number'
    : 'No wasika yet — required before starting trip';
  void flatbedTripId;
  return (
    <button onClick={onOpen} className={'w-full rounded-lg border-2 px-4 py-2 text-sm text-left flex items-center justify-between gap-2 ' + tone}>
      <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> {label}</span>
      <span className="text-xs underline">Open</span>
    </button>
  );
}
